import { IGif } from '@giphy/js-types'
import { PlayerEvents } from '@giphy/react-giphy-brand'
import { trackEvent, VideoGAActions } from 'shared/util/video-ga-events'
import { mapKeys } from 'lodash'
import uuid from 'uuid/v4'

export type quartileOptions = 0.25 | 0.5 | 0.75

export type GAPlayerEventsAPI = {
    getDuration: () => number
    getVideoURL: () => string
    getCurrentTime: () => number
    getIsMutedState: () => boolean
    getLoopCount: () => number
    getVideoData: () => IGif
}

type DefaultDimensions = {
    video_muted: boolean
    video_current_time: number
    video_duration: number
    video_percent: number
    video_url: string
    video_title: string
    media_type: string
    video_session_id: string
    video_stalled_duration?: number
    grid_position?: number
}

const mapDimensions = {
    video_muted: 'dimension14',
    video_current_time: 'dimension16',
    video_duration: 'dimension17',
    video_percent: 'dimension18',
    video_url: 'dimension19',
    video_title: 'dimension20',
    media_type: 'dimension21',
    video_session_id: 'dimension22',
    video_stalled_duration: 'dimension23',
    grid_position: 'dimension24',
}

class VideoPlayerAnalytics implements PlayerEvents {
    videoId: number | string
    isPlaying: boolean
    category?: string
    sessionId: string
    api: GAPlayerEventsAPI
    gif: IGif
    gridPosition?: number

    constructor(videoId: number | string, category?: string, gridPosition?: number) {
        this.videoId = videoId
        this.isPlaying = false
        this.category = category
        this.gridPosition = gridPosition
        this.sessionId = uuid()
    }
    trackAction = (action: VideoGAActions, value?: any) => {
        const dimensions = mapKeys(value, (_, key) => mapDimensions[key] || key)

        trackEvent(action, this.videoId, this.category, dimensions)
    }
    getDefaultDimensions = (): DefaultDimensions | null => {
        // If we don't have video data, do not return dimensions.
        if (!this.api) {
            return null
        }

        const video_duration = this.api.getDuration()
        const video_current_time = this.api.getCurrentTime()

        const defaultDimenions: DefaultDimensions = {
            video_url: this.gif.url,
            video_title: this.gif.title,
            video_duration: this.api.getDuration(),
            video_current_time: this.api.getCurrentTime(),
            video_muted: !!this.api.getIsMutedState(),
            video_percent: video_duration && video_duration > 0 ? video_current_time / video_duration : 0,
            media_type: this.gif.type,
            video_session_id: this.sessionId,
        }

        // If has position on the grid, add to default dimensions
        if (this.gridPosition) {
            defaultDimenions.grid_position = this.gridPosition
        }

        return defaultDimenions
    }
    onPlayerReady = (api: GAPlayerEventsAPI) => {
        this.api = api
        const gif = this.api.getVideoData()
        this.gif = gif
        this.isPlaying = false
    }
    onFirstPlay = (time?: number) => {
        // if has time, track the time of the loading
        this.onPlay()

        if (time) {
            this.trackAction('video_loading', {
                video_stalled_duration: time,
                ...this.getDefaultDimensions(),
            })
        }
    }
    onPlay = () => {
        if (!this.isPlaying) {
            this.isPlaying = true

            this.trackAction('start', {
                ...this.getDefaultDimensions(),
            })
        }
    }
    onPause = () => {
        this.isPlaying = false
        this.trackAction('pause', {
            ...this.getDefaultDimensions(),
        })
    }
    onEnded = () => {
        const loop = this.api.getLoopCount()
        if (loop === 0) {
            this.trackAction('complete', {
                ...this.getDefaultDimensions(),
            })
        }
    }
    onStalledResume = (stalledDuration: number) => {
        // send event on resume with
        this.trackAction('video_stalled', {
            video_stalled_duration: stalledDuration,
            ...this.getDefaultDimensions(),
        })
    }
    onMute = (isMuted: boolean) => {
        this.trackAction(isMuted ? 'mute' : 'unmute', {
            ...this.getDefaultDimensions(),
        })
    }
    onFullscreen = (isFullscreen: boolean) => {
        this.trackAction(isFullscreen ? 'player_expand' : 'player_collapse', {
            ...this.getDefaultDimensions(),
        })
    }
    onShare = (isOpen: boolean) => {
        this.trackAction(isOpen ? 'share_sheet_on' : 'share_sheet_off', {
            ...this.getDefaultDimensions(),
        })
    }
    onShareAction = shareType => {
        this.trackAction(shareType, {
            ...this.getDefaultDimensions(),
        })

        // Track any share for an aggregate count
        this.trackAction('video_shared', {
            ...this.getDefaultDimensions(),
        })
    }
    onCopyLink = () => {
        this.trackAction('copy_link', {
            ...this.getDefaultDimensions(),
        })
    }
    onQuartile = (quartile: quartileOptions) => {
        switch (quartile) {
            case 0.25:
                this.trackAction('first_quartile', {
                    ...this.getDefaultDimensions(),
                })
                break
            case 0.5:
                this.trackAction('midpoint', {
                    ...this.getDefaultDimensions(),
                })
                break
            case 0.75:
                this.trackAction('third_quartile', {
                    ...this.getDefaultDimensions(),
                })
                break
        }
    }
}

export default VideoPlayerAnalytics
